import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router';
import {
    FileCheck02,
    Mail01,
    AlertTriangle,
    CheckCircle,
    Clock,
    Eye
} from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import { quoteService } from '@/data/mock-quotes';
import type { QuoteRequest, QuoteStatus } from '@/types/quote';
import { useTranslation } from 'react-i18next';

interface StatCardProps {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color: 'brand' | 'success' | 'warning' | 'error';
    trend?: { value: number; isPositive: boolean };
    vsLastMonthText?: string;
}

const StatCard = ({ title, value, icon: Icon, color, trend, vsLastMonthText }: StatCardProps) => {
    const colorClasses = {
        brand: 'text-brand-600 bg-brand-50',
        success: 'text-success-600 bg-success-50',
        warning: 'text-warning-600 bg-warning-50',
        error: 'text-error-600 bg-error-50'
    };

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <div className="flex items-center">
                <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
                    <Icon className="size-6" />
                </div>
                <div className="ml-4">
                    <p className="text-sm font-medium text-tertiary">{title}</p>
                    <p className="text-2xl font-semibold text-primary">{value}</p>
                    {trend && vsLastMonthText && (
                        <p className={`text-sm ${trend.isPositive ? 'text-success-600' : 'text-error-600'}`}>
                            {trend.isPositive ? '+' : ''}{trend.value}% {vsLastMonthText}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export const FreightForwarderControlHubPage = () => {
    const { t } = useTranslation(['pages:quotes', 'common']);
    const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            const allQuotes = quoteService.getAll();
            setQuotes(allQuotes);

            setIsLoading(false);
        };
        loadData();
    }, []);

    // Calcola statistiche
    const stats = useMemo(() => {
        const total = quotes.length;
        const pending = quotes.filter(q => q.status === 'pending').length;
        const autoResponded = quotes.filter(q => q.status === 'auto_responded').length;
        const requiresReview = quotes.filter(q => q.status === 'requires_review').length;
        const responded = quotes.filter(q => q.status === 'responded').length;
        const archived = quotes.filter(q => q.status === 'archived').length;

        return {
            total,
            pending,
            autoResponded,
            requiresReview,
            responded,
            archived
        };
    }, [quotes]);

    // Helper per convertire color names a hex
    const getColorHex = (colorName: string): string => {
        switch (colorName) {
            case 'brand': return '#6938ef'; // brand-600
            case 'success': return '#079485'; // success-600
            case 'warning': return '#dc6803'; // warning-600
            case 'error': return '#d92d20'; // error-600
            default: return '#6b7280'; // gray-500
        }
    };

    // Calcola distribuzioni per i grafici
    const distributionData = useMemo(() => {
        const total = quotes.length;

        // Status Distribution
        const statusDistribution = quotes.reduce((acc, q) => {
            acc[q.status] = (acc[q.status] || 0) + 1;
            return acc;
        }, {} as Record<QuoteStatus, number>);

        const statusData = [
            { label: t('status.pending'), value: statusDistribution.pending || 0, color: getColorHex('warning') },
            { label: t('status.autoResponded'), value: statusDistribution.auto_responded || 0, color: getColorHex('success') },
            { label: t('status.requiresReview'), value: statusDistribution.requires_review || 0, color: getColorHex('error') },
            { label: t('status.responded'), value: statusDistribution.responded || 0, color: getColorHex('success') },
            { label: t('status.archived'), value: statusDistribution.archived || 0, color: getColorHex('gray') },
        ];

        // Auto-responded vs Manual
        const autoRespondedCount = quotes.filter(q => q.autoResponded === true).length;
        const manualCount = quotes.filter(q => q.autoResponded === false || q.autoResponded === undefined).length;

        const autoVsManualData = [
            { label: t('dashboard.autoResponded', { ns: 'pages:quotes' }) || 'Auto-responded', value: autoRespondedCount, color: getColorHex('success') },
            { label: t('dashboard.manual', { ns: 'pages:quotes' }) || 'Manual', value: manualCount, color: getColorHex('brand') },
        ];

        return { statusData, autoVsManualData, total };
    }, [quotes, t]);

    const getStatusColor = (status: QuoteStatus) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'auto_responded': return 'success';
            case 'requires_review': return 'error';
            case 'responded': return 'success';
            case 'archived': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: QuoteStatus) => {
        const statusMap: Record<QuoteStatus, string> = {
            'pending': 'status.pending',
            'auto_responded': 'status.autoResponded',
            'requires_review': 'status.requiresReview',
            'responded': 'status.responded',
            'archived': 'status.archived',
            'completed': 'status.completed',
            'cancelled': 'status.cancelled',
        };
        return t(statusMap[status]);
    };

    // Recent quotes (ultime 5)
    const recentQuotes = useMemo(() => {
        return [...quotes]
            .sort((a, b) => b.receivedAt.getTime() - a.receivedAt.getTime())
            .slice(0, 5);
    }, [quotes]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('common:loading') || 'Loading...'}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('controlHub.title', { ns: 'common' })}</h1>
                    <p className="text-tertiary mt-1">
                        {t('dashboard.subtitle', { ns: 'pages:quotes' }) || 'Overview of your quotes and statistics'}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Link
                        to="/freight-forwarder/quotes"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-md font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                    >
                        <Eye className="size-5" />
                        {t('list.viewAll', { ns: 'pages:quotes' }) || 'View All Quotes'}
                    </Link>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title={t('dashboard.stats.total', { ns: 'pages:quotes' }) || 'Total Quotes'}
                    value={stats.total}
                    icon={FileCheck02}
                    color="brand"
                    trend={{ value: 12, isPositive: true }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('dashboard.stats.pending', { ns: 'pages:quotes' }) || 'Pending'}
                    value={stats.pending}
                    icon={Clock}
                    color="warning"
                    trend={{ value: 5, isPositive: false }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('dashboard.stats.autoResponded', { ns: 'pages:quotes' }) || 'Auto-responded'}
                    value={stats.autoResponded}
                    icon={CheckCircle}
                    color="success"
                    trend={{ value: 18, isPositive: true }}
                    vsLastMonthText={t('stats.vsLastMonth', { ns: 'common' })}
                />
                <StatCard
                    title={t('dashboard.stats.requiresReview', { ns: 'pages:quotes' }) || 'Requires Review'}
                    value={stats.requiresReview}
                    icon={AlertTriangle}
                    color="error"
                />
            </div>

            {/* Additional Stats Row */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <StatCard
                    title={t('dashboard.stats.responded', { ns: 'pages:quotes' }) || 'Responded'}
                    value={stats.responded}
                    icon={Mail01}
                    color="success"
                />
                <StatCard
                    title={t('dashboard.stats.archived', { ns: 'pages:quotes' }) || 'Archived'}
                    value={stats.archived}
                    icon={FileCheck02}
                    color="brand"
                />
            </div>

            {/* Distribution Charts */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <DistributionChart
                    title={t('dashboard.charts.statusDistribution', { ns: 'pages:quotes' }) || 'Status Distribution'}
                    data={distributionData.statusData}
                    total={distributionData.total}
                />
                <DistributionChart
                    title={t('dashboard.charts.autoVsManual', { ns: 'pages:quotes' }) || 'Auto-responded vs Manual'}
                    data={distributionData.autoVsManualData}
                    total={distributionData.total}
                />
            </div>

            {/* Recent Quotes Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={t('dashboard.recentQuotes.title', { ns: 'pages:quotes' }) || 'Recent Quotes'}
                    description={t('dashboard.recentQuotes.description', { ns: 'pages:quotes' }) || 'Latest quote requests received'}
                    contentTrailing={
                        <Link
                            to="/freight-forwarder/quotes"
                            className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                        >
                            <Eye className="size-4" />
                            {t('dashboard.recentQuotes.viewAll', { ns: 'pages:quotes' }) || 'View All'}
                        </Link>
                    }
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('list.headers.from', { ns: 'pages:quotes' }) || 'From'} isRowHeader />
                        <Table.Head label={t('list.headers.subject', { ns: 'pages:quotes' }) || 'Subject'} />
                        <Table.Head label={t('list.headers.receivedAt', { ns: 'pages:quotes' }) || 'Received'} />
                        <Table.Head label={t('list.headers.status', { ns: 'pages:quotes' }) || 'Status'} />
                    </Table.Header>
                    <Table.Body>
                        {recentQuotes.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={4} className="text-center text-tertiary py-8">
                                    {t('dashboard.noQuotes', { ns: 'pages:quotes' }) || 'No quotes available'}
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            recentQuotes.map((quote) => (
                                <Table.Row key={quote.id}>
                                    <Table.Cell>
                                        <div className="font-medium text-primary">{quote.fromName || quote.from}</div>
                                        <div className="text-sm text-tertiary">{quote.from}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="text-sm text-primary">{quote.subject}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <span className="text-sm text-tertiary">
                                            {quote.receivedAt.toLocaleDateString('it-IT', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getStatusColor(quote.status)} size="sm">
                                            {translateStatus(quote.status)}
                                        </Badge>
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        )}
                    </Table.Body>
                </Table>
            </TableCard.Root>
        </div>
    );
};

